import { Modal } from "antd";
import { motion } from "framer-motion";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { BsCameraVideo } from "react-icons/bs";
import { FaChevronDown } from "react-icons/fa";
import { FaCode } from "react-icons/fa6";
import { LuPencilLine } from "react-icons/lu";
import { PiBookOpenBold, PiPlayBold } from "react-icons/pi";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { LanguageContext } from "../../contexts/languageContext";
import { Auth } from "../../contexts/ridersUserContext";
import translations from "../../translations";
import { classNames } from "../../utils";
import ModuleIcon from "../shared/icons/icon_module.svg";
import { AssessmentIcon, FileIcon } from "../shared/icons/icons";
import ChevronRightIcon from "../shared/icons/right_chevron.svg";
import { FileSystem, TextArea } from "./";
import { FABMenu } from "./FABMenu";
import { Screen } from "./helpers/Screen";
import { ChapterWorkspaceType } from "./types";
import { getModuleProgresses, submitModuleProgress } from "../../api";
import { getCookie } from "../shared/Cookie";
import { createEncryptor } from "simple-encryptor";
import { Communication } from "../../contexts/communicationContext";
import { useDocumentationFilePath } from "../../contexts/documentationFilePathContext";

const ChapterSideBar: FC<{
  selectChapterWorkspaces: (_workspaces: ChapterWorkspaceType) => void;
  nextButtonRef: any;
}> = ({ selectChapterWorkspaces, nextButtonRef }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    project,
    chapterKey,
    setChapterKey,
    activeChapter,
    setActiveChapter,
    baseYamlData,
    yamlData,
    setData,
    collapsedSidebar,
    setCollapsedSidebar,
  } = useContext<any>(Screen.context);
  const [openedChapter, setOpenedChapter] = useState<string[]>([]);
  const [completedMarkedChapters, setCompletedMarkedChapters] = useState<
    string[]
  >([]);
  const [completedChapters, setCompletedChapters] = useState<string[]>([]);
  const [selectedChapter, setSelectedChapter] = useState<string>("");
  const [chapters, setChapters] = useState<any>([]);
  const { language } = useContext<any>(LanguageContext);
  const { triggeredSubmitQuiz } = useContext<any>(Communication.context);
  const { hasEditor } = useDocumentationFilePath();
  const hasCode = activeChapter?.cells.some((cell: any) => {
    return (
      cell.type === "Code" ||
      (project.documentationUrl ===
        "https://d32lhafmwoak77.cloudfront.net/media/pythonCourse/chapter12/index.html" &&
        cell.type === "Documentation" &&
        hasEditor)
    );
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { publish_branch } = useContext<any>(FileSystem.context);
  const [publishMessage, setPublishMessage] = useState<string>("");
  const [contentLoaded, setContentLoaded] = useState<boolean>(false);

  const initialSearchParamsRef = useRef<string | null>(null);

  useEffect(() => {
    if (triggeredSubmitQuiz !== 0 && yamlData) {
      const chapter = activeChapter?.name || selectedChapter;
      if (chapter) {
        if (yamlData?.chapters) {
          yamlData.chapters.forEach((_chapter: any, _chapterIndex: number) => {
            if (_chapter.name === chapter) {
              handleProgressedModule(_chapter, (_chapterIndex + 1).toString());
              setOpenedChapter([...openedChapter, _chapter.name]);
            }

            _chapter?.subchapters?.forEach(
              (subchapter: any, _subchapterIndex: number) => {
                if (subchapter.name === chapter) {
                  handleProgressedModule(
                    subchapter,
                    (
                      _chapterIndex +
                      1 +
                      (_subchapterIndex + 1) * 0.1
                    ).toString()
                  );
                  setOpenedChapter([...openedChapter, _chapter.name]);
                }
              }
            );
          });
        }
      } else {
        if (progressedChapters.length > 0) {
          const lastProgressedChapterIndex =
            progressedChapters[progressedChapters.length - 1];
          if (yamlData?.chapters) {
            yamlData?.chapters.forEach(
              (_chapter: any, _chapterIndex: number) => {
                if (_chapterIndex + 1 === lastProgressedChapterIndex) {
                  setChapter(_chapter.name, yamlData);
                  setOpenedChapter([...openedChapter, _chapter.name]);
                }
                _chapter?.subchapters?.forEach(
                  (_subchapter: any, _subchapterIndex: number) => {
                    if (
                      _chapterIndex + 1 + (_subchapterIndex + 1) * 0.1 ===
                      lastProgressedChapterIndex
                    ) {
                      setChapter(_subchapter.name, yamlData);
                      setOpenedChapter([...openedChapter, _subchapter.name]);
                    }
                  }
                );
              }
            );
          }
        }
      }

      setUid(uuidv4());
    }
  }, [triggeredSubmitQuiz, yamlData]);

  useEffect(() => {
    // sadece bir kere çalışsın
    if (initialSearchParamsRef.current === null) {
      initialSearchParamsRef.current = window.location.search;
    }
  }, []);

  const [uid, setUid] = useState<string>(uuidv4());

  const { contentEditMode, accessTokenKey, progressedChapters } =
    useContext<any>(Auth.context);

  const toggleOpenChapter = (chapter: string) => {
    if (openedChapter.includes(chapter)) {
      setOpenedChapter(openedChapter.filter((item) => item !== chapter));
    } else {
      setOpenedChapter([...openedChapter, chapter]);
    }
  };

  useEffect(() => {}, [yamlData]);

  useEffect(() => {
    if (yamlData?.chapters) {
      yamlData.chapters.forEach((_chapter: any, index: number) => {
        if (progressedChapters.includes(index + 1)) {
          setCompletedChapters((prev) => [...prev, _chapter.name]);
        }
        _chapter?.subchapters?.forEach((_subchapter: any, subIndex: number) => {
          if (progressedChapters.includes(index + 1 + (subIndex + 1) / 10)) {
            setCompletedMarkedChapters((prev) => [...prev, _subchapter.name]);
          }
        });
      });
    }
  }, [progressedChapters, yamlData]);

  useEffect(() => {
    const currentSearch = window.location.search;
    const initialSearch = initialSearchParamsRef.current;
    if (currentSearch !== initialSearch) return;

    const chapter = searchParams.get("chapter");
    if (chapter) {
      if (yamlData?.chapters) {
        yamlData.chapters.forEach((_chapter: any, _chapterIndex: number) => {
          if (_chapter.name === chapter) {
            if (
              _chapter?.cells?.[0]?.type !== "QA" &&
              _chapter?.icon?.toLowerCase() !== "q&a"
            ) {
              handleProgressedModule(_chapter, (_chapterIndex + 1).toString());
            }

            setOpenedChapter([...openedChapter, _chapter.name]);
          }

          _chapter?.subchapters?.forEach(
            (subchapter: any, _subchapterIndex: number) => {
              if (subchapter.name === chapter) {
                if (
                  subchapter?.cells?.[0]?.type !== "QA" &&
                  subchapter?.icon?.toLowerCase() !== "q&a"
                ) {
                  handleProgressedModule(
                    subchapter,
                    (
                      _chapterIndex +
                      1 +
                      (_subchapterIndex + 1) * 0.1
                    ).toString()
                  );
                }

                setOpenedChapter([...openedChapter, _chapter.name]);
              }
            }
          );
        });
      }
    } else {
      if (progressedChapters.length > 0) {
        const lastProgressedChapterIndex =
          progressedChapters[progressedChapters.length - 1];
        if (yamlData?.chapters) {
          yamlData?.chapters.forEach((_chapter: any, _chapterIndex: number) => {
            if (_chapterIndex + 1 === lastProgressedChapterIndex) {
              setChapter(_chapter.name, yamlData);
              setOpenedChapter([...openedChapter, _chapter.name]);
            }
            _chapter?.subchapters?.forEach(
              (_subchapter: any, _subchapterIndex: number) => {
                if (
                  _chapterIndex + 1 + (_subchapterIndex + 1) * 0.1 ===
                  lastProgressedChapterIndex
                ) {
                  setChapter(_subchapter.name, yamlData);
                  setOpenedChapter([...openedChapter, _subchapter.name]);
                }
              }
            );
          });
        }
      }
    }

    setUid(uuidv4());
  }, [searchParams, yamlData]);

  const setChapter = (_chapters: string, data: any) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      const mainChapter = data?.chapters?.find(
        (_chapter: any) => _chapter.name === _chapters
      );

      // const activeChapter = data?.chapters
      //   ?.map((c: any) => c.subchapters)
      //   .flat()
      //   .find((c: any) => c.name === _chapters)?.name;

      if (mainChapter) {
        newParams.set("chapter", mainChapter?.subchapters[0]?.name);
      } else {
        newParams.set("chapter", _chapters);
      }
      return newParams;
    });
  };
  const goToPrevious = () => {
    const index = chapters.indexOf(activeChapter?.name);
    if (index > 0) {
      setChapter(chapters[index - 1], yamlData);
    }
  };

  const goToNext = () => {
    const index = chapters.indexOf(activeChapter?.name);
    if (index < chapters.length - 1) {
      setChapter(chapters[index + 1], yamlData);
    }
  };

  useEffect(() => {
    if (yamlData) {
      const _chapters: any = [];
      yamlData?.chapters?.forEach((chapter: any) => {
        _chapters.push(chapter.name);
        chapter?.subchapters?.forEach((subchapter: any) => {
          _chapters.push(subchapter.name);
        });
        setChapters(_chapters);
      });
    }
  }, [yamlData]);

  const getIcon = (type: string) => {
    switch (type) {
      case "book":
        return <PiBookOpenBold />;
      case "play":
        return <PiPlayBold />;
      case "code":
        return <FaCode />;
      case "pencil":
        return <LuPencilLine />;
      case "camera":
        return <BsCameraVideo />;
      case "q&a":
        return <AssessmentIcon />;
      case "subject":
        return <FileIcon />;
      default:
        return <PiBookOpenBold />;
    }
  };

  const rerouteParamsChapter = (data: any) => {
    const paramChapter = searchParams.get("chapter");
    if (paramChapter) {
      const _activeChapter: any =
        data.chapters.find((x: any) => {
          return (
            x.name === paramChapter ||
            x.nameEn === paramChapter ||
            x.nameTr === paramChapter
          );
        }) ??
        data.chapters
          .map((chapter: any) =>
            chapter?.subchapters?.find((x: any) => {
              return (
                x.name === paramChapter ||
                x.nameEn === paramChapter ||
                x.nameTr === paramChapter
              );
            })
          )
          .find((x: any) => !!x);
      setChapter(_activeChapter?.name, data);
    }
  };

  useEffect(() => {
    const replaceWithEnglish = (obj: any) => {
      for (let key in obj) {
        if (typeof obj[key] === "object") {
          replaceWithEnglish(obj[key]);
        } else {
          if (key === "nameEn") {
            obj["nameTr"] = obj["name"];
            obj["name"] = obj["nameEn"];
            delete obj["nameEn"];
          }
          if (key === "relativePathEn") {
            obj["relativePath"] = obj["relativePathEn"];
            delete obj["relativePathEn"];
          }
          if (key === "namePrefixEn") {
            obj["namePrefix"] = obj["namePrefixEn"];
            delete obj["namePrefixEn"];
          }
        }
      }
    };

    if (baseYamlData) {
      let jsonData = baseYamlData;
      let unTouchedData = baseYamlData;

      if (language === "EN") {
        replaceWithEnglish(jsonData);
      }

      const modifyChapters = (data: any) => {
        const processChapter = (chapter: any) => {
          if (chapter?.hidden?.includes(language.toLowerCase())) {
            return;
          }
          if (chapter.subchapters && chapter.subchapters.length > 0) {
            chapter.subchapters = chapter.subchapters
              .map((subchapter: any) => processChapter(subchapter))
              .filter((subchapter: any) => subchapter);
          }

          return chapter;
        };

        data.chapters = data.chapters
          .map((chapter: any) => processChapter(chapter))
          .filter((chapter: any) => chapter);
        return data;
      };

      setData(modifyChapters(jsonData));
      rerouteParamsChapter(unTouchedData);
    }
  }, [language]);

  // check if all subchapters are completed
  const checkIfAllSubChaptersAreCompleted = () => {
    let _chaptersCompleted = [...completedMarkedChapters];
    yamlData?.chapters?.forEach((chapter: any, index: any) => {
      if (chapter?.subchapters) {
        const el = chapter?.subchapters?.map((subchapter: any) => {
          if (!_chaptersCompleted.includes(subchapter.name)) {
            return subchapter.name;
          }
        });
        el.filter((item: any) => item !== undefined).length === 0 &&
          !completedChapters.includes(chapter.name) &&
          setCompletedChapters((prev) => [...prev, chapter.name]);
      }
    });
  };

  const selectChapter = (chapter: any, indexString?: string) => {
    setChapter(chapter.name, yamlData);
    if (
      indexString &&
      chapter?.cells?.[0]?.type !== "QA" &&
      chapter?.icon?.toLowerCase() !== "q&a"
    ) {
      handleProgressedModule(chapter, indexString);
    }
  };

  const encryptor = createEncryptor("simpleKeyForSimpleEncryptor");

  const encryptMessage = useCallback((message: any) => {
    if (typeof message === "string") {
      return encryptor.encrypt(message);
    } else if (typeof message === "object") {
      return encryptor.encrypt(JSON.stringify(message));
    }
    return "ERROR";
  }, []);

  const handleProgressedModule = async (chapter: any, indexString: string) => {
    if (parseInt(indexString) < 0) return;

    try {
      await submitModuleProgress(
        getCookie(accessTokenKey) as string,
        encryptMessage(indexString)
      );
    } catch {}
  };

  useEffect(() => {
    const chapter = searchParams.get("chapter");
    if (chapter) {
      setCompletedMarkedChapters((prev) => [...prev, chapter]);
    }
  }, [searchParams]);

  const checkChapterWorkspace = (
    chapter: any,
    comparison: "Documentation" | "Powerpoint" | "Simulation" | "Code" | "QA"
  ) => {
    return (
      chapter.cells.filter((item: any) => item.type === comparison).length > 0
    );
  };

  useEffect(() => {
    if (yamlData?.chapters) {
      checkIfAllSubChaptersAreCompleted();
    }
  }, [completedMarkedChapters]);

  // cater for initial load without selecting chapters
  useEffect(() => {
    const chapter = searchParams.get("chapter");
    if (yamlData?.chapters) {
      if (!chapter && progressedChapters.length === 0) {
        setChapter(yamlData.chapters[0].name, yamlData);
      }
    }
    //eslint-disable-next-line
  }, [searchParams, yamlData, progressedChapters]);

  useEffect(() => {
    if (activeChapter) {
      selectChapterWorkspaces({
        powerpoint: checkChapterWorkspace(activeChapter, "Powerpoint"),
        documentation: checkChapterWorkspace(activeChapter, "Documentation"),
        code: checkChapterWorkspace(activeChapter, "Code"),
        simulation: checkChapterWorkspace(activeChapter, "Simulation"),
        QA: checkChapterWorkspace(activeChapter, "QA"),
      });
    }
    //eslint-disable-next-line
  }, [chapterKey]);

  // set active chapter
  useEffect(() => {
    if (searchParams.get("chapter") && yamlData?.chapters) {
      if (activeChapter?.name !== searchParams.get("chapter")) {
        const _activeChapter: any =
          yamlData.chapters.find((x: any) => {
            x.name === searchParams.get("chapter") &&
              setSelectedChapter(x.name);
            return x.name === searchParams.get("chapter");
          }) ??
          yamlData.chapters
            .map((chapter: any) =>
              chapter?.subchapters?.find((x: any) => {
                x.name === searchParams.get("chapter") &&
                  setSelectedChapter(chapter.name);
                return x.name === searchParams.get("chapter");
              })
            )
            .find((x: any) => !!x);
        setActiveChapter(_activeChapter);
        setChapterKey(uuidv4());

        setContentLoaded(true);
      }
    }

    //eslint-disable-next-line
  }, [searchParams, yamlData]);

  const collapseChapter = () => {
    setCollapsedSidebar(true);
  };

  const onAnimationComplete = () => {
    const chapterSidebar = document.getElementById("chapter-sidebar");
    if (collapsedSidebar) {
      chapterSidebar?.style.setProperty("display", "none");
    }
  };

  const onAnimationStart = () => {
    const chapterSidebar = document.getElementById("chapter-sidebar");
    if (!collapsedSidebar) {
      chapterSidebar?.style.setProperty("display", "initial");
    }
  };

  const blockedChapters = [
    "Robotik Kavramına Giriş",
    "Mbot Setini Tanıyalım",
    "Mbot Motor Uygulamaları",
  ];
  const blockedProject = project.defaultCode === "disable_modules = True";
  const checkpoint = "Robotun İleri Hareketi";

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handlePublish = async () => {
    try {
      await publish_branch(publishMessage);
      setIsModalOpen(false);
    } catch (e) {
      console.error(e);
    } finally {
      setPublishMessage("");
    }
  };

  return (
    <>
      <div id="side-bar-container" className="flex flex-col">
        {hasCode && <FABMenu hasCode={hasCode} showModal={showModal} />}
        {collapsedSidebar && (
          <button
            onClick={() => setCollapsedSidebar(false)}
            className="bg-[#C3D8F3] dark:bg-vs-dark-background-lighter text-vs-dark-background dark:text-vs-dark-text-lighter h-8 w-5 rounded-md flex items-center justify-center mt-7"
          >
            <img
              src={ChevronRightIcon}
              alt="module icon"
              className="object-contain w-5 h-5 dark:invert dark:brightness-0"
            />
          </button>
        )}
        <motion.div
          id="chapter-sidebar"
          initial={{ width: 250, opacity: 1 }}
          animate={{
            width: !collapsedSidebar ? 250 : 0,
            opacity: collapsedSidebar ? 0 : 1,
          }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ ease: "easeInOut", duration: 0.2 }}
          onAnimationComplete={onAnimationComplete}
          onAnimationStart={onAnimationStart}
          className="relative h-full w-[250px] pt-6 pb-0 bg-white dark:bg-vs-dark-background-lighter rounded-[30px] text-editor-text flex-none overflow-hidden"
        >
          {!collapsedSidebar && (
            <motion.div
              key="expandedContent"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className="overflow-y-auto h-full pb-24 px-4"
            >
              <div className="side-bar flex items-start justify-between mb-4 bg-white dark:bg-vs-dark-background-lighter dark:text-vs-dark-text sticky top-0 z-[999]">
                {/*<h1 className='text-[#4F5774] font-medium text-xl px-2'>Modules</h1>*/}
                <div className="w-11/12 pb-4">
                  <h1 className="text-[#6E85D9] font-medium text-sm px-2">
                    {yamlData?.namePrefix}
                  </h1>
                  <p className="text-[#4F5774] dark:text-vs-dark-text pl-2 text-[1.1rem]">
                    {yamlData?.name ?? "Project Name"}
                  </p>
                </div>
                <button
                  className="w-8 h-8 bg-editor-background dark:bg-vs-dark-background rounded-md flex items-center justify-center"
                  onClick={collapseChapter}
                >
                  <img
                    src={ModuleIcon}
                    alt="module icon"
                    className="object-contain w-5 h-5 dark:invert dark:brightness-0"
                  />
                </button>
              </div>
              <ol className="space-y-3">
                {yamlData?.chapters?.map((chapter: any, index: any) => {
                  return (
                    <div
                      key={index}
                      className={classNames(
                        selectedChapter === chapter.name
                          ? "bg-[#E3F0FF] dark:bg-vs-dark-background"
                          : "bg-editor-chapter-bg dark:bg-[#304057]",
                        "rounded-[30px] relative w-full py-2 px-2"
                      )}
                    >
                      <li
                        className={classNames(
                          blockedProject &&
                            blockedChapters.includes(chapter.name)
                            ? "cursor-default text-gray-400 dark:text-[#BCC5E7]"
                            : "hover:text-riders-green dark:text-[#BCC5E7] cursor-pointer text-white, dark:bg-transparent text-editor-title",
                          "text-base flex items-center space-x-2  font-medium py-2 px-3 rounded-md duration-200"
                        )}
                        onClick={() => {
                          if (!chapter?.subchapters) {
                            setCompletedChapters([
                              ...completedChapters,
                              chapter.name,
                            ]);
                          }

                          if (!blockedChapters.includes(chapter.name)) {
                            selectChapter(chapter, index + 1);
                          }

                          if (
                            activeChapter.name === chapter?.subchapters[0].name
                          ) {
                            toggleOpenChapter(chapter.name);
                          }
                        }}
                      >
                        <span
                          className={classNames(
                            completedChapters.includes(chapter.name)
                              ? "bg-riders-green border-riders-green flex justify-center items-center"
                              : "border-editor-blue bg-editor-blue",
                            "w-6 h-6 border-2 text-white inline-block rounded-full flex-none flex justify-center items-center"
                          )}
                        >
                          {getIcon(chapter.icon ?? "subject")}
                        </span>{" "}
                        <span className="leading-6 pr-5">
                          {contentEditMode ? index : index + 1}. {chapter.name}
                        </span>
                      </li>
                      {chapter?.subchapters &&
                        openedChapter.includes(chapter.name) && (
                          <ol className="space-y-1 mt-2 px-1.5 pb-2">
                            {chapter?.subchapters?.map(
                              (subchapter: any, subIndex: number) => (
                                <li
                                  key={subchapter.name}
                                  className={classNames(
                                    searchParams.get("chapter") ===
                                      subchapter.name
                                      ? "bg-[#C8E2FF] dark:bg-vs-dark-background-lighter"
                                      : "bg-transparent dark:hover:text-opacity-60",
                                    "text-sm dark:text-vs-dark-text-lighter leading-5 font-medium cursor-pointer py-2 px-3 space-x-2 rounded-full hover:text-riders-green duration-200 flex items-center"
                                  )}
                                  onClick={() =>
                                    selectChapter(
                                      subchapter,
                                      `${index + 1}.${subIndex + 1}`
                                    )
                                  }
                                >
                                  <span
                                    className={classNames(
                                      completedMarkedChapters.includes(
                                        subchapter.name
                                      )
                                        ? "bg-riders-green"
                                        : completedMarkedChapters.includes(
                                            subchapter.name
                                          )
                                        ? "bg-riders-green"
                                        : "bg-editor-blue",
                                      subchapter.name === checkpoint
                                        ? "ring-4 ring-[#9CFB00]"
                                        : "",
                                      "w-7 h-7 p-2 rounded-full flex items-center justify-center text-white"
                                    )}
                                  >
                                    {/*{getIcon(subchapter.icon)}*/}
                                    {/*TODO: Remove this in production*/}
                                    {getIcon(
                                      subchapter.name === checkpoint
                                        ? "code"
                                        : subchapter.icon
                                    )}
                                  </span>
                                  <span className="subChapterName">
                                    {contentEditMode ? index : index + 1}.
                                    {subIndex + 1}. {subchapter.name}
                                  </span>
                                </li>
                              )
                            )}
                          </ol>
                        )}
                      {chapter?.subchapters && (
                        <FaChevronDown
                          onClick={() => toggleOpenChapter(chapter.name)}
                          className={classNames(
                            openedChapter.includes(chapter.name)
                              ? "rotate-180"
                              : "",
                            "absolute right-5 top-6 text-editor-title dark:text-white cursor-pointer hover:text-opacity-80 duration-300"
                          )}
                        />
                      )}
                    </div>
                  );
                })}
              </ol>
            </motion.div>
          )}
          <div
            className="bg-white rounded-b-[30px] flex justify-between absolute z-20 items-center bottom-0 left-0 px-4 py-4 w-full dark:bg-vs-dark-background-lighter"
            key={uid}
          >
            {/*Previous*/}
            {chapters.indexOf(activeChapter?.name) > 0 ? (
              <button
                onClick={goToPrevious}
                className="h-8 text-white duration-300 flex items-center justify-center bg-[#A7BBD4] py-1 px-2 cursor-pointer rounded-full"
              >
                <BiChevronLeft size={30} />
              </button>
            ) : (
              <div />
            )}
            {/* <Dropdown disabled={!hasCode} menu={<MainMenu showModal={showModal} />} position='bottom-10 -left-full'>
              <Tooltip placement="right" title={<FormattedMessage id='code'/>} color='white' overlayInnerStyle={{color: 'black', fontFamily: 'Alata'}} className='h-auto !flex'>
                <button disabled={!hasCode} className={classNames(!hasCode ? 'bg-gray-300' : 'bg-riders-green', 'rounded-full py-2 px-4 flex items-center justify-center text-white')}>
                  <UserCode/>
                </button>
              </Tooltip>
            </Dropdown> */}

            {/*Next*/}
            {chapters.indexOf(activeChapter?.name) < chapters.length - 1 ? (
              <button
                id="chapter-sidebar-next"
                ref={nextButtonRef}
                onClick={goToNext}
                className="flex text-white items-center justify-center duration-300 bg-[#059BCA] py-1 px-3 pl-5 cursor-pointer rounded-full"
              >
                <span>
                  <FormattedMessage id="buttonNext" />
                </span>
                <BiChevronRight size={30} />
              </button>
            ) : (
              <div className="w-10" />
            )}
          </div>
          <Modal
            title={<FormattedMessage id="publish" />}
            open={isModalOpen}
            okButtonProps={{
              className: "!bg-riders-green font-alata",
              disabled: !publishMessage.trim(),
            }}
            onOk={handlePublish}
            cancelText={<FormattedMessage id="cancel" />}
            okText={<FormattedMessage id="publish" />}
            onCancel={handleCancel}
            className="font-alata"
            cancelButtonProps={{
              className:
                "hover:!border-riders-green hover:!text-riders-green font-alata",
            }}
          >
            <TextArea
              className="!bg-white border"
              rows={3}
              onChange={(e: any) => {
                setPublishMessage(e.target.value);
              }}
              value={publishMessage}
              placeholder={translations[language]["publishTextAreaMessage"]}
              block
            />
          </Modal>
        </motion.div>
      </div>
    </>
  );
};

export default ChapterSideBar;
